// Panel derecho de un código o cupón ya creado: el mismo editor del alta, deshabilitado, porque el
// back no los edita. «Anular» va en el menú del encabezado y no hay barra de guardar.
import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { ActionMenu, CopyableId } from '@/kit';
import { EditorField, EditorSection } from '@/shared/patterns/EditorForm';
import { LifecycleEditorShell, LifecycleNotice } from '@/shared/patterns/Lifecycle';
import type { DiscountCode } from '../api/promotionsContract';
import type { PromotionFormSources } from '../api/usePromotionFormSources';
import { DISCOUNT_CODE_HELP } from '../constants/helpText';
import { discountCodeToFormValues, type DiscountCodeFormValues } from '../lib/discountCodeForm';
import {
    formatCodeUses,
    formatCodeValidity,
    formatOrigin,
    isVoidable,
    kindLabel,
    listCodeStores,
    redeemedIn,
} from '../lib/discountCodesRow';
import { PROMOTIONS_PERMISSIONS } from '../permissions';
import { CodeStatusBadge } from './CodeStatusBadge';
import { DiscountCodeFields, ReadOnlyValue } from './DiscountCodeFields';

export interface DiscountCodeDetailProps {
    code: DiscountCode;
    sources: Pick<PromotionFormSources, 'products' | 'productsState' | 'stores' | 'storesState'>;
    /** Día CDMX `YYYY-MM-DD` contra el que se pintan los calendarios. */
    today: string;
    onVoid: (code: DiscountCode) => void;
}

const NONE = '—';

function SaleLink({ saleId }: { saleId: string | null }) {
    if (saleId === null) return <>{NONE}</>;
    return (
        <Link
            to="/retail-admin/$section"
            params={{ section: 'ventas' }}
            search={{ venta: saleId }}
            className="font-mono text-primary underline-offset-2 hover:underline"
        >
            {saleId}
        </Link>
    );
}

/** Un solo uso enlaza la venta; varios usos, cuántas ventas lo canjearon. */
function RedeemedIn({ code }: { code: DiscountCode }) {
    const value = redeemedIn(code);
    if (value.kind === 'text') return <span className="tabular-nums">{value.text}</span>;
    return <SaleLink saleId={value.saleId} />;
}

const NO_SAVE = { count: 0, onDiscard: () => undefined, onSave: () => undefined };

export function DiscountCodeDetail({ code, sources, today, onVoid }: DiscountCodeDetailProps) {
    const isCoupon = code.origin === 'SALE_COUPON';
    const form = useForm<DiscountCodeFormValues>({ values: discountCodeToFormValues(code) });
    const storeNames = useMemo(
        () => new Map(sources.stores.map((store) => [store.storeId, store.name])),
        [sources.stores],
    );

    return (
        <LifecycleEditorShell
            ariaLabel={`${kindLabel(code)} ${code.codeText}`}
            title={<span className="font-mono">{code.codeText}</span>}
            dirty={false}
            subtitle={`${kindLabel(code)} · ${formatCodeValidity(code)}`}
            status={null}
            tags={<CodeStatusBadge status={code.status} />}
            id={{ label: 'ID', value: code.codeId }}
            actions={
                isVoidable(code) ? (
                    <ActionMenu
                        label={`Acciones de ${code.codeText}`}
                        items={[
                            {
                                label: 'Anular',
                                destructive: true,
                                permission: PROMOTIONS_PERMISSIONS.codeVoid,
                                onSelect: () => {
                                    onVoid(code);
                                },
                            },
                        ]}
                    />
                ) : null
            }
            notices={
                <LifecycleNotice>
                    {isCoupon
                        ? 'El cupón lo emite una venta y solo lo usa su dueño, una vez. No se edita; solo se anula.'
                        : 'Un código creado ya no se edita; si algo cambió, anúlalo y crea otro.'}
                </LifecycleNotice>
            }
            saveBar={NO_SAVE}
        >
            <fieldset disabled className="m-0 min-w-0 border-0 p-0">
                <DiscountCodeFields
                    form={form}
                    sources={sources}
                    today={today}
                    origin={formatOrigin(code)}
                    codeLabel={isCoupon ? 'Cupón' : 'Código'}
                    readOnly
                    storesText={
                        code.couponScope === null ? undefined : listCodeStores(code, storeNames)
                    }
                    rulesExtra={
                        <>
                            <EditorField label="Usos" help={DISCOUNT_CODE_HELP.uses}>
                                <ReadOnlyValue>
                                    <span className="tabular-nums">{formatCodeUses(code)}</span>
                                </ReadOnlyValue>
                            </EditorField>
                            <EditorField label="Canjeado en" help={DISCOUNT_CODE_HELP.redeemedIn}>
                                <ReadOnlyValue>
                                    <RedeemedIn code={code} />
                                </ReadOnlyValue>
                            </EditorField>
                        </>
                    }
                />
            </fieldset>
            {isCoupon ? (
                <EditorSection title="Cupón" help={DISCOUNT_CODE_HELP.coupon}>
                    <div className="grid grid-cols-2 gap-3">
                        <EditorField
                            label="Venta que lo emitió"
                            help={DISCOUNT_CODE_HELP.issuingSale}
                        >
                            <ReadOnlyValue>
                                <SaleLink saleId={code.issuedBySaleId} />
                            </ReadOnlyValue>
                        </EditorField>
                        <EditorField
                            label="Tienda que lo emitió"
                            help={DISCOUNT_CODE_HELP.issuingStore}
                        >
                            <ReadOnlyValue>
                                {code.issuingStoreId === null
                                    ? NONE
                                    : (storeNames.get(code.issuingStoreId) ?? code.issuingStoreId)}
                            </ReadOnlyValue>
                        </EditorField>
                        <EditorField label="Dueño" help={DISCOUNT_CODE_HELP.owner}>
                            <ReadOnlyValue>
                                {/* El contrato no trae el nombre del cliente: va su id copiable. */}
                                {code.ownerCustomerId === null ? (
                                    NONE
                                ) : (
                                    <CopyableId
                                        inline
                                        label="Cliente"
                                        value={code.ownerCustomerId}
                                    />
                                )}
                            </ReadOnlyValue>
                        </EditorField>
                    </div>
                </EditorSection>
            ) : null}
        </LifecycleEditorShell>
    );
}
